// Source: "AICA Grade - Current AICA Risk Framework.csv"
// Maps each scored parameter to its importance weighting in the framework.
// Used by RiskParameters and Score modules to surface the importance level
// alongside the severity so a SEVERE rating on a Critical parameter reads
// differently than the same rating on a Low one.

export type ParameterImportance = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export const IMPORTANCE_META: Record<ParameterImportance, { label: string; weight: number; abbr: string }> = {
    CRITICAL: { label: 'Critical', weight: 100, abbr: 'C' },
    HIGH: { label: 'High', weight: 75, abbr: 'H' },
    MEDIUM: { label: 'Medium', weight: 50, abbr: 'M' },
    LOW: { label: 'Low', weight: 25, abbr: 'L' },
};

// Keys are normalized parameter display names (lowercased, alphanumerics only).
// Lookups are tolerant of casing, punctuation and minor wording differences.
const PARAMETER_IMPORTANCE: Record<string, ParameterImportance> = {
    // Business Operations
    revenue: 'CRITICAL',
    revenuegrowth: 'HIGH',
    businessvintage: 'MEDIUM',
    employeestrengthandheadcountincreasereduction: 'MEDIUM',
    employeestrength: 'MEDIUM',
    headcountincreasereduction: 'MEDIUM',
    fundingstage: 'HIGH',
    relatedpartyrisk: 'HIGH',

    // Financial
    currentratio: 'HIGH',
    quickratio: 'HIGH',
    cashratio: 'MEDIUM',
    debtequityratio: 'HIGH',
    interestcoverageratio: 'HIGH',
    netprofitmargin: 'CRITICAL',
    roereturnonequity: 'CRITICAL',
    roe: 'CRITICAL',
    returnonequity: 'CRITICAL',
    roareturnonassets: 'CRITICAL',
    roa: 'CRITICAL',
    returnonassets: 'CRITICAL',
    ebitdamargin: 'CRITICAL',
    grossmargin: 'HIGH',
    assetturnover: 'MEDIUM',
    inventoryturnover: 'MEDIUM',
    workingcapitalcycle: 'MEDIUM',
    freecashflow: 'HIGH',
    freecashflowgrowth: 'MEDIUM',

    // Management
    managementconsistency: 'HIGH',
    yearsofworkexperience: 'MEDIUM',
    managingmultiplebusinesses: 'MEDIUM',
    bureauscore: 'HIGH',
    politicallyexposedpersoncheck: 'HIGH',
    pep: 'HIGH',

    // Governance
    indirecttaxcompliancegstcompliance: 'CRITICAL',
    indirecttaxcompliance: 'CRITICAL',
    gstcompliance: 'CRITICAL',
    epfcompliance: 'HIGH',
    changeinauditors: 'HIGH',

    // Legal and Regulatory
    legalcasesfiledbythecompany: 'MEDIUM',
    legalcasesfiledbydirector: 'HIGH',
    legalcasesagainstthecompany: 'CRITICAL',
    legalcasesagainstthedirector: 'CRITICAL',
    customercomplaints: 'HIGH',
    amlsanctions: 'CRITICAL',
    globalsanctions: 'CRITICAL',
    indiawatchlists: 'HIGH',
    stateownedenterprises: 'MEDIUM',
    countryregimeswatchlists: 'HIGH',
    adversemediacoverage: 'HIGH',
};

const normalizeKey = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

// Longer-first ordering so wording-drift like "Revenue Growth Score" resolves to
// `revenuegrowth` (HIGH) before the shorter `revenue` key (CRITICAL) wins.
const PARAMETER_IMPORTANCE_BY_LENGTH: Array<[string, ParameterImportance]> = Object.entries(PARAMETER_IMPORTANCE).sort(
    (a, b) => b[0].length - a[0].length,
);

export function getParameterImportance(displayName: string | null | undefined): ParameterImportance | null {
    if (!displayName) return null;
    const key = normalizeKey(displayName);
    if (!key) return null;
    if (PARAMETER_IMPORTANCE[key]) return PARAMETER_IMPORTANCE[key];
    // Substring fallback — most-specific (longest) key wins.
    for (const [k, v] of PARAMETER_IMPORTANCE_BY_LENGTH) {
        if (key.includes(k) || k.includes(key)) return v;
    }
    return null;
}
